import { Children, Fragment, isValidElement, type ReactElement, type ReactNode } from 'react';
import { useIsWideViewport } from '@hue/design-system';
import { useCollectionView } from './use-collection-view.js';

type NodeProps = {
  children?: ReactNode;
  label?: string;
  className?: string;
  onClick?: () => void;
  colSpan?: number;
  column?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  onToggle?: (column: string) => void;
};
function nodes(children: ReactNode): ReactElement<NodeProps>[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement<NodeProps>(child)) return [];
    return child.type === Fragment ? nodes(child.props.children) : [child];
  });
}
function textOf(children: ReactNode): string {
  return Children.toArray(children)
    .map((child): string => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      return isValidElement<NodeProps>(child)
        ? (child.props.label ?? textOf(child.props.children))
        : '';
    })
    .join(' ');
}

/** Reutiliza as células e ações da tabela em cartões; nenhum dado ou regra é duplicado. */
export function AdaptiveTable({ children }: { children: ReactElement<NodeProps> }) {
  const view = useCollectionView();
  const wide = useIsWideViewport();
  if (view === 'list' && wide) return children;
  const parts = nodes(children.props.children);
  const head = parts.find((part) => part.type === 'thead');
  const body = parts.find((part) => part.type === 'tbody');
  const headers = nodes(nodes(head?.props.children)[0]?.props.children);
  const sortable = headers.filter((header) => header.props.onToggle && header.props.column);
  return (
    <div className="grid gap-3">
      {sortable.length ? (
        <div role="group" aria-label="Ordenação dos registros" className="flex flex-wrap gap-2">
          {sortable.map((header, index) => (
            <button
              key={index}
              type="button"
              className="rounded-lg border border-surface-border px-3 py-2 text-xs"
              aria-pressed={header.props.sort === header.props.column}
              onClick={() => header.props.onToggle?.(header.props.column!)}
            >
              {header.props.label}
              {header.props.sort === header.props.column
                ? header.props.direction === 'asc'
                  ? ' ↑'
                  : ' ↓'
                : ''}
            </button>
          ))}
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        {nodes(body?.props.children).map((row, index) => (
          <article
            key={row.key ?? index}
            className="min-w-0 rounded-xl border border-surface-border bg-surface-card p-4"
            onClick={(event) => {
              if (!(event.target instanceof Element && event.target.closest('button, a, input')))
                row.props.onClick?.();
            }}
          >
            <dl className="grid gap-3 sm:grid-cols-2">
              {nodes(row.props.children).map((cell, cellIndex) => {
                const label =
                  textOf(headers[cellIndex]?.props.children) ||
                  headers[cellIndex]?.props.label ||
                  '';
                return (
                  <div
                    key={cell.key ?? cellIndex}
                    className={
                      label === 'Ações' || cell.props.colSpan ? 'sm:col-span-2' : 'min-w-0'
                    }
                  >
                    {label && label !== 'Ações' ? (
                      <dt className="mb-1 text-xs text-content-muted">{label}</dt>
                    ) : null}
                    <dd
                      className={
                        label === 'Ações' ? 'flex justify-end gap-1' : 'break-words text-sm'
                      }
                    >
                      {cell.props.children}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </article>
        ))}
      </div>
    </div>
  );
}
